import { Box } from '@material-ui/core';
import {
  DocumentationRequiredType,
  getRequiredFileTypesByDocumentationRequiredType,
  RequiredFile,
} from '@nemuru-eng/js-api-client';

import {
  AddRequiredFileToDocumentationRequiredSchema,
  AddRequiredFileToDocumentationRequiredType,
} from '@/components/modules/Documentation/components/DocumentationRequiredHeader/schemas/AddRequiredFileToDocumentationRequiredSchema';
import { LoadingValuesWarning } from '@/components/modules/Documentation/components/shared/LoadingValuesWarning';
import { RequiredFileTypesOptions } from '@/components/modules/Documentation/constants/RequiredFileTypesOptions';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCache } from '@/hooks/requests/useCache';
import { compareTwoArraysAndRemoveObjectWithEqualKeyValues } from '@/utils/compareTwoArraysAndRemoveObjectWithEqualKeyValues';

interface AddRequiredFileDialogProps {
  opened: boolean;
  setOpened: (boolean) => void;
  onSubmitForm: (values: AddRequiredFileToDocumentationRequiredType) => Promise<void>;
  handleClose: () => void;
  selectedDocumentationRequiredType: DocumentationRequiredType;
  requiredFiles: RequiredFile[] | null;
  loading: boolean;
}

export const AddRequiredFileDialog = ({
  opened,
  setOpened,
  selectedDocumentationRequiredType,
  handleClose,
  onSubmitForm,
  requiredFiles,
  loading,
}: AddRequiredFileDialogProps) => {
  const { getTextIdTranslation } = useTranslation();
  const documentationRequiredFileTypes = useCache(
    getRequiredFileTypesByDocumentationRequiredType,
    {
      documentationRequiredType: selectedDocumentationRequiredType,
    },
    {
      revalidateIfStale: false,
    },
  ).data?.sort((a, b) =>
    (getTextIdTranslation(a.name) as string).localeCompare(getTextIdTranslation(b.name) as string),
  );

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      title={'ADD_REQUIRED_FILE_TYPE'}
      onSubmitForm={onSubmitForm}
      saveButtonTextId={'REQUEST'}
      formSchema={AddRequiredFileToDocumentationRequiredSchema}
      isLoading={loading}
    >
      <>
        <Box mb={4}>
          <Font variant="subtitle2" color="textSecondary">
            <Text tId={'ADD_REQUIRED_FILE_TYPE_DESCRIPTION'} />
          </Font>
        </Box>
        {documentationRequiredFileTypes ? (
          <AutocompleteSelectFormField
            name={'fileTypeId'}
            label={'REQUIRED_DOCUMENT_TYPE'}
            selectOptions={RequiredFileTypesOptions(
              compareTwoArraysAndRemoveObjectWithEqualKeyValues('name', documentationRequiredFileTypes, requiredFiles),
            )}
          />
        ) : (
          <LoadingValuesWarning messageTextId={'LOADING_FILE_TYPES'} />
        )}
      </>
    </EditDialogWrapper>
  );
};
